import PropTypes from 'prop-types';
import { useCallback, useEffect, useRef, useState } from 'react';

// The scrollbar of a long career window, drawn by the widget instead of the browser (so it looks the same everywhere and can be
// seen on any colour): an arrow at the top, a rail with a thumb that can be dragged, and an arrow at the bottom.
//
//     ⌃     press: scroll a little (hold: keep scrolling)
//     │     click the rail: scroll a page toward the click
//     █     drag the thumb
//     │
//     ⌄
//
// The element to scroll is `targetRef`; its own scrollbar is hidden by the stylesheet. It only shows while there is more to see.
const STYLES = `
.tsw-sb { position:var(--sb-pos, fixed); top:10px; bottom:10px; right:8px; width:26px; z-index:20000; display:flex; flex-direction:column; align-items:center; gap:6px; user-select:none; -webkit-user-select:none; touch-action:none; }
.tsw-sb.on-dark { --sb-fg:#fff; --sb-btn:rgba(255,255,255,.14); --sb-btn-hover:rgba(255,255,255,.28); --sb-line:rgba(255,255,255,.42); --sb-track:rgba(255,255,255,.2); --sb-thumb:rgba(255,255,255,.9); }
.tsw-sb.on-light { --sb-fg:#12151c; --sb-btn:rgba(18,21,28,.06); --sb-btn-hover:rgba(18,21,28,.14); --sb-line:rgba(18,21,28,.3); --sb-track:rgba(18,21,28,.12); --sb-thumb:rgba(18,21,28,.72); }
.tsw-sb-btn { appearance:none; flex:0 0 26px; width:26px; height:26px; padding:0; border-radius:8px; border:1px solid var(--sb-line); background:var(--sb-btn); color:var(--sb-fg); display:inline-flex; align-items:center; justify-content:center; cursor:pointer; transition:background .15s, opacity .15s; }
.tsw-sb-btn:hover:not(:disabled) { background:var(--sb-btn-hover); }
.tsw-sb-btn:disabled { opacity:.35; cursor:default; }
.tsw-sb-btn:focus-visible, .tsw-sb-thumb:focus-visible { outline:2px solid #f9a748; outline-offset:2px; }
.tsw-sb-btn svg { width:14px; height:14px; }
.tsw-sb-track { position:relative; flex:1 1 auto; min-height:0; width:8px; border-radius:6px; background:var(--sb-track); cursor:pointer; }
.tsw-sb-thumb { position:absolute; left:0; right:0; border-radius:6px; background:var(--sb-thumb); cursor:grab; transition:background .15s, transform .15s; }
.tsw-sb-track:hover .tsw-sb-thumb { transform:scaleX(1.25); }
.tsw-sb-thumb.is-dragging { background:#f9a748; cursor:grabbing; transform:scaleX(1.25); }
@media (max-width:600px) { .tsw-sb { right:2px; width:20px; top:8px; bottom:8px; } .tsw-sb-btn { flex-basis:20px; width:20px; height:20px; border-radius:6px; } .tsw-sb-btn svg { width:11px; height:11px; } .tsw-sb-track { width:6px; } }
@media (prefers-reduced-motion: reduce) { .tsw-sb *, .tsw-sb { transition:none !important; } }
`;

const Chevron = ({ up }) => (
  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" style={up ? { transform: 'rotate(180deg)' } : undefined}>
    <path d="M5 7.5 10 12.5 15 7.5" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
Chevron.propTypes = { up: PropTypes.bool };

const MIN_THUMB = 40;
const DEFAULT_TARGET_ID = 'tsw-scroll-target';
const STEP = 140;

const CustomScrollbar = ({ targetRef, scheme, placement, targetId = DEFAULT_TARGET_ID }) => {
  const trackRef = useRef(null);
  const frame = useRef(null);
  const hold = useRef({ delay: null, repeat: null });
  const drag = useRef(null);
  const [metrics, setMetrics] = useState({ scrollable: false, top: 0, height: MIN_THUMB, atTop: true, atBottom: false, ratio: 0 });
  const [dragging, setDragging] = useState(false);

  const update = useCallback(() => {
    frame.current = null;
    const element = targetRef.current;
    const track = trackRef.current;
    if (!element) return;
    const room = element.scrollHeight - element.clientHeight;
    const trackHeight = track ? track.clientHeight : 0;
    const height = Math.min(Math.max((trackHeight * element.clientHeight) / Math.max(element.scrollHeight, 1), MIN_THUMB), Math.max(trackHeight, MIN_THUMB));
    const ratio = room > 0 ? element.scrollTop / room : 0;
    setMetrics({
      scrollable: room > 8,
      top: Math.max(trackHeight - height, 0) * ratio,
      height,
      atTop: element.scrollTop <= 2,
      atBottom: element.scrollTop >= room - 2,
      ratio,
    });
  }, [targetRef]);
  const schedule = useCallback(() => {
    if (frame.current === null) frame.current = window.requestAnimationFrame(update);
  }, [update]);

  useEffect(() => {
    const element = targetRef.current;
    if (!element) return undefined;
    if (!element.id) element.id = targetId;
    update();
    element.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    // the content changes once the career has loaded and when a course or a tab is opened
    const mutations = typeof MutationObserver !== 'undefined' ? new MutationObserver(schedule) : null;
    if (mutations) mutations.observe(element, { childList: true, subtree: true, attributes: true });
    const resizes = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(schedule) : null;
    if (resizes) resizes.observe(element);
    return () => {
      element.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      if (mutations) mutations.disconnect();
      if (resizes) resizes.disconnect();
      if (frame.current !== null) window.cancelAnimationFrame(frame.current);
    };
  }, [targetRef, targetId, update, schedule, metrics.scrollable]);

  const stopHold = () => {
    window.clearTimeout(hold.current.delay);
    window.clearInterval(hold.current.repeat);
  };
  useEffect(() => stopHold, []);
  const step = (direction, behavior) => {
    if (targetRef.current) targetRef.current.scrollBy({ top: direction * STEP, behavior });
  };
  const startHold = (direction) => {
    step(direction, 'smooth');
    stopHold();
    hold.current.delay = window.setTimeout(() => {
      hold.current.repeat = window.setInterval(() => step(direction, 'auto'), 40);
    }, 350);
  };

  const onTrackPointerDown = (event) => {
    if (event.target !== trackRef.current) return;
    const element = targetRef.current;
    const box = trackRef.current.getBoundingClientRect();
    const direction = event.clientY < box.top + metrics.top ? -1 : 1;
    element.scrollBy({ top: direction * element.clientHeight * 0.85, behavior: 'smooth' });
  };
  const onThumbPointerDown = (event) => {
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    drag.current = { y: event.clientY, scrollTop: targetRef.current.scrollTop };
    setDragging(true);
  };
  const onThumbPointerMove = (event) => {
    if (!drag.current) return;
    const element = targetRef.current;
    const travel = trackRef.current.clientHeight - metrics.height;
    if (travel <= 0) return;
    const room = element.scrollHeight - element.clientHeight;
    element.scrollTop = drag.current.scrollTop + ((event.clientY - drag.current.y) / travel) * room;
  };
  const onThumbPointerUp = () => {
    drag.current = null;
    setDragging(false);
  };
  const onThumbKeyDown = (event) => {
    const element = targetRef.current;
    const keys = { ArrowUp: -STEP, ArrowDown: STEP, PageUp: -element.clientHeight * 0.85, PageDown: element.clientHeight * 0.85 };
    if (event.key in keys) {
      event.preventDefault();
      element.scrollBy({ top: keys[event.key], behavior: 'smooth' });
    } else if (event.key === 'Home') {
      element.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (event.key === 'End') {
      element.scrollTo({ top: element.scrollHeight, behavior: 'smooth' });
    }
  };

  if (!metrics.scrollable && !targetRef.current) return null;
  return (
    <div className={`tsw-sb ${scheme === 'onLight' ? 'on-light' : 'on-dark'}`} style={{ '--sb-pos': placement === 'absolute' ? 'absolute' : 'fixed', display: metrics.scrollable ? undefined : 'none' }}>
      <style>{STYLES}</style>
      <button
        type="button"
        className="tsw-sb-btn"
        aria-label="Scroll up"
        disabled={metrics.atTop}
        onPointerDown={() => startHold(-1)}
        onPointerUp={stopHold}
        onPointerLeave={stopHold}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            step(-1, 'smooth');
          }
        }}
      >
        <Chevron up />
      </button>
      <div className="tsw-sb-track" ref={trackRef} onPointerDown={onTrackPointerDown}>
        <div
          className={`tsw-sb-thumb${dragging ? ' is-dragging' : ''}`}
          role="scrollbar"
          aria-label="Scroll position"
          aria-controls={targetId}
          aria-orientation="vertical"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(metrics.ratio * 100)}
          tabIndex={0}
          style={{ top: metrics.top, height: metrics.height }}
          onPointerDown={onThumbPointerDown}
          onPointerMove={onThumbPointerMove}
          onPointerUp={onThumbPointerUp}
          onPointerCancel={onThumbPointerUp}
          onKeyDown={onThumbKeyDown}
        />
      </div>
      <button
        type="button"
        className="tsw-sb-btn"
        aria-label="Scroll down"
        disabled={metrics.atBottom}
        onPointerDown={() => startHold(1)}
        onPointerUp={stopHold}
        onPointerLeave={stopHold}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            step(1, 'smooth');
          }
        }}
      >
        <Chevron />
      </button>
    </div>
  );
};

CustomScrollbar.propTypes = {
  targetRef: PropTypes.shape({ current: PropTypes.any }).isRequired,
  scheme: PropTypes.string,
  placement: PropTypes.string,
  targetId: PropTypes.string,
};

export default CustomScrollbar;
